import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AppLayout from '@/components/feature/AppLayout';
import BlockchainCanvas from '@/components/feature/BlockchainCanvas';
import { useAuthContext } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useApiKeys } from '@/hooks/useApiKeys';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
});

export default function DashboardPage() {
  const { user } = useAuthContext();
  const { profile } = useProfile();
  const { stats, revenueData, topProducts, recentActivity, loading } = useDashboardStats();
  const { apiKeys } = useApiKeys();
  const location = useLocation();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const firstActiveKey = apiKeys.find(k => k.status === 'active');

  const copyApiKey = () => {
    if (!firstActiveKey) return;
    navigator.clipboard.writeText(firstActiveKey.key_prefix + '••••••••');
    setCopiedKey(firstActiveKey.id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const displayName = user?.username
    ? user.username.charAt(0).toUpperCase() + user.username.slice(1)
    : 'there';

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const [forbiddenBanner, setForbiddenBanner] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('forbidden') === 'admin') {
      setForbiddenBanner(true);
      const t = setTimeout(() => setForbiddenBanner(false), 5000);
      return () => clearTimeout(t);
    }
  }, [location.search]);

  const statCards = [
    {
      label: 'Total Revenue',
      value: loading ? '—' : `$${stats.totalRevenue.toLocaleString()}`,
      change: stats.revenueChange,
      icon: 'ri-money-dollar-circle-line',
      color: 'text-brand-500',
      bg: 'bg-brand-50',
    },
    {
      label: 'Total Orders',
      value: loading ? '—' : stats.totalOrders.toLocaleString(),
      change: stats.ordersChange,
      icon: 'ri-shopping-cart-line',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Active Subscriptions',
      value: loading ? '—' : stats.activeSubscriptions.toLocaleString(),
      change: stats.subscriptionsChange,
      icon: 'ri-key-2-line',
      color: 'text-amber-500',
      bg: 'bg-amber-50',
    },
    {
      label: 'API Calls (Month)',
      value: loading ? '—' : stats.apiCallsMonth >= 1000000
        ? `${(stats.apiCallsMonth / 1000000).toFixed(1)}M`
        : stats.apiCallsMonth >= 1000
        ? `${(stats.apiCallsMonth / 1000).toFixed(1)}K`
        : stats.apiCallsMonth.toLocaleString(),
      change: stats.apiCallsChange,
      icon: 'ri-terminal-line',
      color: 'text-slate-600',
      bg: 'bg-slate-100',
    },
  ];

  return (
    <AppLayout>
      <AnimatePresence>
        {forbiddenBanner && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-2"
          >
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
              <i className="ri-shield-keyhole-line text-red-500 text-base" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700">Access Denied — Admin Only</p>
              <p className="text-xs text-red-500 mt-0.5">You don&apos;t have permission to access the Admin CMS.</p>
            </div>
            <button onClick={() => setForbiddenBanner(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 cursor-pointer">
              <i className="ri-close-line text-red-400 text-sm" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-6">
        {/* Blockchain Network Hero */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative bg-sidebar rounded-2xl overflow-hidden h-44">
          <div className="absolute inset-0"><BlockchainCanvas /></div>
          <div className="absolute inset-0 flex flex-col justify-center px-8">
            <div className="text-xs font-mono text-brand-400 mb-1 tracking-widest uppercase">Live Network</div>
            <h2 className="text-2xl font-bold text-white">
              {greeting}, {displayName}
              {user?.role === 'admin' && (
                <span className="ml-3 align-middle text-[11px] font-semibold bg-brand-500/30 text-brand-300 border border-brand-400/30 px-2.5 py-1 rounded-full">
                  <i className="ri-shield-star-line mr-1" />Admin
                </span>
              )}
            </h2>
            <p className="text-sm text-white/60 mt-1">ONE API. INFINITE TRANSACTIONS. ZERO FRICTION.</p>
            <div className="flex items-center gap-4 mt-3">
              {[
                { label: 'Network Uptime', value: '99.97%', color: 'text-green-400' },
                { label: 'Avg Latency', value: '94ms', color: 'text-brand-400' },
                {
                  label: 'Member Since',
                  value: profile?.joined_at
                    ? new Date(profile.joined_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : '—',
                  color: 'text-white',
                },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-white/40">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute top-4 right-5 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[11px] text-white/80 font-mono">LIVE</span>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div {...fadeUp(0.08)} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Copy API Key */}
          <button
            onClick={copyApiKey}
            disabled={!firstActiveKey}
            className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border transition-all cursor-pointer whitespace-nowrap group ${firstActiveKey ? 'border-surface-3 hover:border-brand-300 hover:bg-brand-50/30' : 'border-surface-3 opacity-60 cursor-not-allowed'}`}
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-50 shrink-0">
              <i className={`${copiedKey ? 'ri-check-line text-green-500' : 'ri-key-2-line text-brand-500'} text-base`} />
            </span>
            <div className="text-left flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900">
                {copiedKey ? 'Copied!' : 'Copy API Key'}
              </div>
              <div className="text-[11px] text-gray-400 truncate">
                {firstActiveKey ? firstActiveKey.key_prefix + '…' : 'No active key — go to Developer'}
              </div>
            </div>
            <i className={`ri-arrow-right-line text-gray-300 text-sm group-hover:text-brand-400 transition-colors ${copiedKey ? 'hidden' : ''}`} />
          </button>

          {/* Go to Marketplace */}
          <a href="/marketplace" className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-surface-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all cursor-pointer whitespace-nowrap group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50 shrink-0">
              <i className="ri-store-2-line text-emerald-500 text-base" />
            </span>
            <div className="text-left flex-1">
              <div className="text-xs font-semibold text-gray-900">Browse Marketplace</div>
              <div className="text-[11px] text-gray-400">Discover premium APIs</div>
            </div>
            <i className="ri-arrow-right-line text-gray-300 text-sm group-hover:text-emerald-400 transition-colors" />
          </a>

          {/* My Orders */}
          <a href="/orders" className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-surface-3 hover:border-amber-300 hover:bg-amber-50/30 transition-all cursor-pointer whitespace-nowrap group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-50 shrink-0">
              <i className="ri-shopping-bag-3-line text-amber-500 text-base" />
            </span>
            <div className="text-left flex-1">
              <div className="text-xs font-semibold text-gray-900">My Orders</div>
              <div className="text-[11px] text-gray-400">Track payment status</div>
            </div>
            <i className="ri-arrow-right-line text-gray-300 text-sm group-hover:text-amber-400 transition-colors" />
          </a>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s, i) => (
            <motion.div key={s.label} {...fadeUp(i * 0.07)} className="bg-white rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.bg}`}>
                  <i className={`${s.icon} text-xl ${s.color}`} />
                </span>
                {loading ? (
                  <div className="h-5 w-12 bg-gray-100 rounded-full animate-pulse" />
                ) : (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.change >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                    {s.change >= 0 ? '+' : ''}{s.change}%
                  </span>
                )}
              </div>
              {loading ? (
                <div className="h-7 w-20 bg-gray-100 rounded animate-pulse mb-1" />
              ) : (
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              )}
              <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <motion.div {...fadeUp(0.2)} className="lg:col-span-2 bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-semibold text-gray-900">Revenue Overview</h3>
                <p className="text-xs text-gray-400 mt-0.5">Your monthly spending trend</p>
              </div>
              <span className="text-xs bg-surface-2 border border-surface-3 px-3 py-1 rounded-full text-gray-500">Last 6 months</span>
            </div>
            {loading ? (
              <div className="h-[220px] bg-gray-50 rounded-xl animate-pulse" />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff7f0a" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#ff7f0a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${v}`, 'Revenue']} contentStyle={{ borderRadius: 8, border: '1px solid #f0f0f0', fontSize: 12 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#ff7f0a" strokeWidth={2} fill="url(#revenueGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Top Products */}
          <motion.div {...fadeUp(0.25)} className="bg-white rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">My Products</h3>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-4 h-3 bg-gray-100 rounded" />
                    <div className="flex-1 space-y-1">
                      <div className="h-3 bg-gray-100 rounded w-3/4" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                    </div>
                    <div className="h-3 bg-gray-100 rounded w-12" />
                  </div>
                ))}
              </div>
            ) : topProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <i className="ri-inbox-line text-2xl text-gray-300 mb-2" />
                <p className="text-xs text-gray-400">No purchases yet.</p>
                <p className="text-xs text-gray-400">Visit the marketplace to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topProducts.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="text-xs font-mono text-gray-300 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{p.name}</div>
                      <div className="text-[11px] text-gray-400">{p.sales} {p.sales === 1 ? 'order' : 'orders'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-gray-900">${p.revenue.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Recent Activity */}
        <motion.div {...fadeUp(0.3)} className="bg-white rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-surface-3 last:border-0 animate-pulse">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-100 rounded w-4/5" />
                    <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <i className="ri-history-line text-3xl text-gray-200 mb-3" />
              <p className="text-sm text-gray-500 font-medium">No activity yet</p>
              <p className="text-xs text-gray-400 mt-1">Your orders and transactions will appear here.</p>
            </div>
          ) : (
            <div className="space-y-0">
              {recentActivity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 py-3 border-b border-surface-3 last:border-0">
                  <span className={`w-8 h-8 flex items-center justify-center rounded-lg bg-surface-2 shrink-0 ${a.color}`}>
                    <i className={`${a.icon} text-base`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-relaxed">{a.message}</p>
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{a.time}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </AppLayout>
  );
}
